/*
 * @Author: zxf
 * @Date: 2021-11-24 18:03:04
 * @Description: 捕捉行为类的基类，不同的捕捉行为具有不同的捕捉数据结构.
 * @FilePath: \neolix-map-sdk\src\snap\SnapActor.js
 */
import * as THREE from "../../libs/three.js/build/three.module.js";
import {CSS2DObject} from "../../libs/three.js/renderers/CSS2DRenderer.js";
import {SceneRegister} from "../register/SceneRegister.js";
import {GemmetryAlgorithm} from "../algorithm/geometry.js";
import { RenderController } from "../render/RenderController.js";
import {TextSprite} from "../TextSprite.js";
import {Utils} from "../utils.js";
import { SnapController } from "./SnapController";
export default class SnapActor {
    /**
     * 捕捉行为类初始化方法.
     * @returns {undefined}
     */
    constructor(options) {
        this.type = '';
        this.priority = options.priority;
        this.threshold = options.threshold || 5;
        this.point = null;
        this.isSnapped = false;
        this.isDrawFeedback = true;
        this.snapDistance = options.snapDistance || Number.MAX_VALUE;
        this.viewer = options.viewer;
		this.scene = options.viewer.scene;
		this.renderer = options.viewer.renderer;
		this.domElement = this.renderer.domElement;
		this.sceneRegister = SceneRegister.getInstance();
        this.feedbackScene = this.sceneRegister.scenes.feedback;
        this.mouseInfoSymbol = null;
        this.feedback = new THREE.Object3D();
		this.gemmetryAlgorithm = GemmetryAlgorithm.getInstance();
    }
    
    /**
     * @Author: zxf
     * @description: 
     * @param {*}
     * @return {*}
     */
    getSnapBox(point, threshold = this.threshold) {
        return turf.polygon([[
			[point.x - threshold, point.y - threshold],
			[point.x - threshold, point.y + threshold],
			[point.x + threshold, point.y + threshold],
			[point.x + threshold, point.y - threshold],
			[point.x - threshold, point.y - threshold]
		]])
    }

    /**
     * @Author: zxf
     * @description: 获取鼠标
     * @param {*}
     * @return {*}
     */
    getMousePoint(point) {
        return turf.point([ point.x ,point.y]);
    }

    /**
     * 根据鼠标移动的位置，以及构造出来的查找范围捕捉该范围内的数据.
     * @mehtod snap
     * @param {Object} point - 鼠标移动的点集合
     * @param {Object} box - 查找的范围的面几何
     * @returns {Object} - 捕捉到的数据结果.
     */
    snap(point, box) {
        this.snapBox = box;
        this.isSnapped = false;
        this.point = null;
        return this.getSnapResult();
    }

    /**
     * 绘制当前捕捉的捕捉符号反馈.
     * @return {undefined}
     */
    draw(snapResult, mousePoint) {
        this.drawClear();
        const renderController = RenderController.getInstance();
        const renderReg = renderController.renderReg;
        const snapController = SnapController.getInstance();
        if(snapResult.length > 0 && snapResult[0].type != 'snapPointCloud') {
            const currSnap = snapController.getCurrSnap();
            this.drawSnapSymbol(snapResult);
            if(snapResult.length > 1) {
                this.setMouseLableGeo(mousePoint, currSnap);
            }
            this.drawFeatureHigh(renderReg, currSnap);
        }
    }

    /**
     * 绘制当前捕捉范围
     * @return {undefined}
     */
    drawSnapBox() {
        if (!this.feedback) {
            return;
        }

        const symbol = this.symbolFactory.getSymbol('py_face');
        this.feedback.add(this.snapBox, symbol);
    }

    drawClear() {
        this.feedback.clear();
        this.mouseInfoSymbol.setText('');
        const renderController = RenderController.getInstance();
        renderController.setStatus(true);
    }

    /**
     * 在当前捕捉位置绘制捕捉符号
     * @return {undefined}
     */
    drawSnapSymbol(snapResult) {
        let point = snapResult[0].snapPoint;
        if (!this.feedback) {
            return;
        }
        const renderController = RenderController.getInstance();
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute([0,0,0], 3));
        const pointMaterial = new THREE.PointsMaterial( { color: 0xffa500, size: 12, sizeAttenuation: false } );
        const feedbackPoint = new THREE.Points(geometry, pointMaterial);
        feedbackPoint.position.copy(point);
        this.feedback.add(feedbackPoint);
        renderController.setStatus(true);
    }

    drawFeatureHigh(renderReg, currSnap) {
        if(currSnap.type == 'snapFeature') {
            const featureType = currSnap.feature.type;
            if(renderReg[featureType].getSnapHighSymbol) {
                const renders = renderReg[featureType].getSnapHighSymbol(currSnap.feature);
                const renderController = RenderController.getInstance();
                if(renders) {
                    if(Array.isArray(renders)) {
                        renders.forEach(element => {
                            this.feedback.add(element);
                        });
                    } else {
                        this.feedback.add(renders);
                    }
                    renderController.setStatus(true);
                }
            }
        }
    }

    createMouseLabel() {
        const mouseLabel = document.createElement( 'div' );
        mouseLabel.className = 'label label-snap';
        mouseLabel.textContent = '';
        mouseLabel.contentEditable = false;
        const textLabel = new CSS2DObject( mouseLabel );
        this.mouseInfoSymbol = textLabel;
        this.feedbackScene.add(this.mouseInfoSymbol);
    }

    setMouseLableGeo(e, currSnap) {
        this.mouseInfoSymbol.setText('');
        const renderController = RenderController.getInstance();
        let point = Utils.getWorldCoordinate(this.domElement, this.scene, e.x, e.y - 20, 0);
        const camera = this.scene.getActiveCamera();
        this.mouseInfoSymbol.position.copy(point);
        let distance = camera.position.distanceTo(this.mouseInfoSymbol.getWorldPosition(new THREE.Vector3()));
        let pr = Potree.Utils.projectedRadius(1, camera, distance, this.domElement.clientWidth, this.domElement.clientHeight);
        let scale = (70 / pr);
        if (Potree.debug.scale) {
            scale = (Potree.debug.scale / pr);
        }
        
        if(currSnap.type == 'snapFeature') {
            this.mouseInfoSymbol.setText(`${currSnap.feature.type} : ${currSnap.feature.id}`);
        }
        this.mouseInfoSymbol.scale.set(scale, scale, scale);
		renderController.setStatus(true);
    }

    /**
     * 清空捕捉要行为反馈对象的符号
     * 并更新捕捉反馈,实时绘制捕捉光标.
     * @return {undefined}
     */
    clear() {
        this.feedback.clear();
        this.mouseInfoSymbol.setText('');
    }

    /**
     * 关闭当前捕捉行为,在反馈容器中删除该反馈行为对应的反馈对象
     * 并更新地图上的反馈.
     * @return {undefined}
     */
    shutdown() {
        this.feedback.clear();
        this.feedbackScene.remove(this.feedback);
        this.feedbackScene.remove(this.mouseInfoSymbol);
    }

    /**
     * 开始捕捉要素数据
     * 并更新捕捉反馈,实时绘制捕捉光标.
     * @return {undefined}
     */
    startup() {
        this.feedbackScene.add(this.feedback);
        this.createMouseLabel();
    }

    /**
     * 获得计算得到的最终捕捉数据结构.
     * @returns {Object}.
     */
    getSnapResult() {
        if (!this.isSnapped) {
            return null;
        }

        return {
            type: this.type,
            point: this.point
        };
    }

    /**
     * 获得该捕捉的优先级.
     * @returns {number} priority
     */
    getPriority() {
        return this.priority;
    }

    /**
     * 设置捕捉器是否绘制捕捉反馈.
     * @param {Boolean} value - true或者false
     * @returns {undefined}
     */
    setIsDrawFeedback(value) {
        this.isDrawFeedback = value;
    }

    /**
     * 获取捕捉器是否绘制捕捉反馈.
     * @returns {Boolean} map - 返回isDrawFeedback
     */
    getIsDrawFeedback() {
        return this.isDrawFeedback;
    }

    /**
     * 设置捕捉器是否绘制捕范围.
     * @param {Boolean} value - true或者false
     * @returns {undefined}
     */
    setIsDrawSnapBox(value) {
        this.isDrawSnapBox = value;
    }

    /**
     * 获取捕捉器是否绘制捕捉范围.
     * @returns {Boolean} map - 返回isDrawSnapBox
     */
    getIsDrawSnapBox() {
        return this.isDrawSnapBox;
    }

    /**
     * 计算鼠标移动点和目标几何之间的距离.
     * @param {Object} geometry1 - 鼠标点位几何
     * @param {Object} geometry2 - 目标对象几何
     * @returns {Object} - 返回计算的结果对象
     */
    distance(geometry1, geometry2) {
        this.geojsonTransform.setEnviroment(this.map, null, this.latlngToMercator);
        const pGeometry1 = this.geojsonTransform.convertGeometry(geometry1);
        const pGeometry2 = this.geojsonTransform.convertGeometry(geometry2);
        return this.geometryAlgorithm.distance(pGeometry1, pGeometry2);
    }

    /**
     * 计算两点和之间的球面距离.
     * @param {Object} geometry1 - 点位几何
     * @param {Object} geometry2 - 点位几何
     * @returns {Object} - 返回计算的结果对象
     */
    sphericalDistance(geometry1, geometry2) {
        return this.geometryAlgorithm.sphericalDistance(geometry1, geometry2);
    }

    /**
     * 计算鼠标点位距离捕捉线数据几何最近的点.
     * @param {Object} geometry1 - 鼠标点位几何
     * @param {Object} geometry2 - 目标线对象几何
     * @returns {Object} - 返回计算的结果对象
     */
    nearestPoints(geometry1, geometry2) {
        const res = this.geometryAlgorithm.nearestPoints(geometry1, geometry2);
        return res;
    }

    /**
     * 计算鼠标点位距离捕捉要素数据几何最近的点.
     * @param {Object} geometry1 - 鼠标点位几何
     * @param {Object} geometry2 - 目标对象几何
     * @returns {Object} - 返回计算的结果对象
     */
    nearestLocations(geometry1, geometry2) {
        this.geojsonTransform.setEnviroment(this.map, null, this.latlngToMercator);
        const pGeometry1 = this.geojsonTransform.convertGeometry(geometry1);
        const pGeometry2 = this.geojsonTransform.convertGeometry(geometry2);
        const res = this.geometryAlgorithm.nearestLocations(pGeometry1, pGeometry2);
        this.geojsonTransform.setEnviroment(this.map, null, this.mercatorToLatlng);
        res.point = this.geojsonTransform.convertGeometry(res.point);
        res.previousPoint = this.geojsonTransform.convertGeometry(res.previousPoint);
        res.nextPoint = this.geojsonTransform.convertGeometry(res.nextPoint);
        return res;
    }

    /**
     * 判断两个几何是否相交.
     * @param  {Object} geometry1 - 传入的几何对象
     * @param  {Object} geometry2 - 传入的几何对象
     * @return {Boolean} - 布尔值，反应两个几何是否相交
     */
    intersects(geometry1, geometry2) {
        this.geojsonTransform.setEnviroment(this.map, null, this.latlngToMercator);
        const pGeometry1 = this.geojsonTransform.convertGeometry(geometry1);
        const pGeometry2 = this.geojsonTransform.convertGeometry(geometry2);
        return this.geometryAlgorithm.intersects(pGeometry1, pGeometry2);
    }
}
